// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/QuestionForm.test.tsx
import { Badge, Button, Flex, Text } from "@radix-ui/themes";
import cx from "classnames";
import { type ReactNode } from "react";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type QuestionFormCardAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Suffix after `{testIdPrefix}-action-`. Submit keeps `question-submit-btn`. */
  action: string;
};

export type QuestionFormCardProps = {
  title?: string;
  detail1?: string;
  detail2?: string;
  detail3?: string;
  children?: ReactNode;
  primary?: QuestionFormCardAction;
  secondary?: QuestionFormCardAction;
  tertiary?: QuestionFormCardAction;
  /** Outer card testid. Product defaults to `question-card`. */
  testId?: string;
  /**
   * Prefix for title / details / slot / action children.
   * why: SLOP-T-15 — every repeated child must be prefixed, not only the wrapper.
   */
  testIdPrefix?: string;
  questionIndex?: number;
  className?: string;
};

function actionTestId(prefix: string, action: QuestionFormCardAction): string {
  if (action.action === "submit") return "question-submit-btn";
  return `${prefix}-action-${action.action}`;
}

/**
 * Optional Title / Details / Slot / Action Row on the existing question-card chrome.
 * why: Carrie card tokens (gray-6 border, gray-1 fill, shadow-sm) — do not reintroduce Figma glow.
 */
export function QuestionFormCard({
  title,
  detail1,
  detail2,
  detail3,
  children,
  primary,
  secondary,
  tertiary,
  testId = "question-card",
  testIdPrefix = "question-form",
  questionIndex,
  className,
}: QuestionFormCardProps) {
  const hasDetails = Boolean(detail1 || detail2 || detail3);
  const hasSlot = children != null;
  const hasActions = Boolean(primary || secondary || tertiary);
  const hasBody = hasDetails || hasSlot || hasActions;

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.questionFormCard)}
      data-testid={testId}
      data-question-index={questionIndex}
      className={cx(
        "rounded-xl border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1 p-4 shadow-sm",
        className,
      )}
    >
      {title ? (
        <Text
          as="p"
          size="2"
          weight="medium"
          data-testid={`${testIdPrefix}-title`}
          className="text-puntt-neutral-gray-12"
        >
          {title}
        </Text>
      ) : null}

      {hasBody ? (
        <div
          data-testid={`${testIdPrefix}-body`}
          className={cx("flex flex-col gap-3", title && "mt-3")}
        >
          {hasDetails ? (
            <div
              data-testid={`${testIdPrefix}-details`}
              className="flex flex-col gap-1"
            >
              {detail1 ? (
                <Badge
                  size="1"
                  variant="soft"
                  color="blue"
                  data-testid={`${testIdPrefix}-detail-1`}
                >
                  {detail1}
                </Badge>
              ) : null}
              {detail2 ? (
                <Text
                  as="p"
                  size="1"
                  data-testid={`${testIdPrefix}-detail-2`}
                  className="text-puntt-neutral-gray-10"
                >
                  {detail2}
                </Text>
              ) : null}
              {detail3 ? (
                <Text
                  as="p"
                  size="1"
                  data-testid={`${testIdPrefix}-detail-3`}
                  className="text-puntt-neutral-gray-10"
                >
                  {detail3}
                </Text>
              ) : null}
            </div>
          ) : null}

          {hasSlot ? (
            <div data-testid={`${testIdPrefix}-slot`} className="min-w-0">
              {children}
            </div>
          ) : null}

          {hasActions ? (
            <Flex
              gap="2"
              wrap="wrap"
              justify="end"
              data-testid={`${testIdPrefix}-action-row`}
            >
              {primary ? (
                <Button
                  data-testid={actionTestId(testIdPrefix, primary)}
                  size="2"
                  variant="solid"
                  color="blue"
                  disabled={primary.disabled}
                  onClick={primary.onClick}
                >
                  {primary.label}
                </Button>
              ) : null}
              {secondary ? (
                <Button
                  data-testid={actionTestId(testIdPrefix, secondary)}
                  size="2"
                  variant="outline"
                  disabled={secondary.disabled}
                  onClick={secondary.onClick}
                >
                  {secondary.label}
                </Button>
              ) : null}
              {tertiary ? (
                <Button
                  data-testid={actionTestId(testIdPrefix, tertiary)}
                  size="2"
                  variant="ghost"
                  disabled={tertiary.disabled}
                  onClick={tertiary.onClick}
                >
                  {tertiary.label}
                </Button>
              ) : null}
            </Flex>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
